import Dialog from '@pinecast/common/Dialog';
import ErrorState from '@pinecast/common/ErrorState';
import {useMountEffect} from '@pinecast/common/hooks/useMountEffect';
import Link from '@pinecast/common/Link';
import LoadingState from '@pinecast/common/LoadingState';
import ModalLayer from '@pinecast/common/ModalLayer';
import Section from '@pinecast/common/Section';
import {BlockSkeleton, InlineSkeleton} from '@pinecast/common/Skeleton';
import {DesktopPageHeader} from '@pinecast/common/Text';
import ChromeSidebar, {
  ChromeSidebarItem,
} from '@pinecast/db-spa/App/ChromeSidebar';
import {usePageTitleEffect} from '@pinecast/db-spa/App/usePageTitle';
import {useManagedMutation, useQuery} from '@pinecast/gql';
import type {
  RecurringDonation,
  RecurringDonation_tipJarSubscription,
  RecurringDonationVariables,
} from '@pinecast/gql/__generated_tj__/RecurringDonation';
import type {
  RecurringDonationSupplemental,
  RecurringDonationSupplementalVariables,
} from '@pinecast/gql/__generated_tj__/RecurringDonationSupplemental';
import type {
  SaveCardSetupSession,
  SaveCardSetupSessionVariables,
} from '@pinecast/gql/__generated_tj__/SaveCardSetupSession';
import {defineMessage, FormattedMessage} from '@pinecast/i18n';
import {useColors, useCSS} from '@pinecast/styles';
import gql from 'graphql-tag';
import * as React from 'react';
import {useNavigate, useLocation, useParams} from 'react-router-dom';

import Chrome from '../Chrome';

import RecurringDonationHeader from './RecurringDonationHeader';
import RecurringDonationPageFeedbackInner from './RecurringDonationPageFeedback';
import RecurringDonationPageListenEpisodeInner from './RecurringDonationPageListen';
import RecurringDonationPagePrivateContent from './RecurringDonationPagePrivateContent';
import RecurringDonationPageUpdatesInner from './RecurringDonationPageUpdates';
import RecurringDonationPaymentMethod from './RecurringDonationPaymentMethod';
import {
  RECURRING_DONATION,
  RECURRING_DONATION_SUPPLEMENTAL,
} from './recurringDonationQuery';
import RecurringDonationReceiptsInner from './RecurringDonationReceipts';

const SAVE_CARD_SESSION = gql`
  mutation SaveCardSetupSession($slug: ID!, $sessionId: String!) {
    saveCardSetupSession(podcastSlug: $slug, sessionId: $sessionId) {
      sessionId
    }
  }
`;

const messages = {
  loadingTitle: defineMessage({
    description: 'Title when podcast is loading',
    defaultMessage: 'Recurring tip',
  }),
  error: defineMessage({
    description: 'Title when podcast could not be loaded',
    defaultMessage: 'Unknown subscription',
  }),
  title: defineMessage({
    description:
      'Title when podcast is loaded. {podcast} is the name of the podcast',
    defaultMessage: 'Subscription to {podcast}',
  }),
  overviewTab: defineMessage({
    description: 'Tab title for payment info for a podcast subscription',
    defaultMessage: 'Payment',
  }),
  privateTab: defineMessage({
    description: 'Tab title for episodes for a podcast subscription',
    defaultMessage: 'Listen',
  }),
  feedbackTab: defineMessage({
    description: 'Tab title to leave a comment for a podcast',
    defaultMessage: 'Leave a comment',
  }),
  updatesTab: defineMessage({
    description: 'Tab title for updates from a podcast the listener supports',
    defaultMessage: 'Updates',
  }),
  receiptsTab: defineMessage({
    description: 'Header for receipts for a podcast subscription',
    defaultMessage: 'Receipts',
  }),
};

const useGetId = () => {
  const {id} = useParams<{id: string}>();
  if (!id) {
    throw new Error('Missing required route parameter: id');
  }
  return id;
};

const useHandleSaveCardSetupSession = () => {
  const id = useGetId();
  const navigate = useNavigate();
  const location = useLocation();
  const [savingCardSession, setSavingCardSession] = React.useState(
    !!new URLSearchParams(location.search).get('card_session_id'),
  );

  const saveCardSetupSession = useManagedMutation<
    SaveCardSetupSession,
    SaveCardSetupSessionVariables
  >(SAVE_CARD_SESSION);

  useMountEffect(async () => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('card_session_id')!;
    if (!sessionId) {
      return;
    }
    setSavingCardSession(true);
    await saveCardSetupSession.action({variables: {sessionId, slug: id}});
    setSavingCardSession(false);
    navigate(location.pathname, {replace: true});
  });
  return (
    <ModalLayer canEscape={false} open={savingCardSession}>
      <Dialog
        title={
          <FormattedMessage
            description="Heading shown while saving a payment card"
            defaultMessage="Update payment method"
          />
        }
      >
        <LoadingState
          title={
            <FormattedMessage
              description="Message shown while saving a payment card"
              defaultMessage="Saving payment data…"
            />
          }
        />
      </Dialog>
    </ModalLayer>
  );
};

const RecurringDonationPageWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const css = useCSS();
  const designColors = useColors();

  const {id, query} = useRecurringDonationPage();
  usePageTitleEffect(() => {
    if (query.loading) {
      return [messages.loadingTitle];
    }
    if (query.error || !query.data.tipJarSubscription) {
      return [messages.error];
    }
    return [
      messages.title,
      {podcast: query.data.tipJarSubscription.podcast.name},
    ];
  }, [query]);

  const cardSetupSessionUI = useHandleSaveCardSetupSession();

  return (
    <Chrome
      returnLink={{
        text: (
          <FormattedMessage
            description="Link to return from subscription page to subscription list"
            defaultMessage="Return to subscriptions"
          />
        ),
        href: '/subscriptions/recurring_tips',
      }}
    >
      {cardSetupSessionUI}
      <div
        className={css({
          alignItems: 'flex-start',
          display: 'flex',
          paddingBottom: '32px',
          paddingTop: '24px',
        })}
      >
        {query.loading ? (
          <BlockSkeleton
            style={{height: '32px', width: '32px', marginRight: '8px'}}
          />
        ) : query.error || !query.data.tipJarSubscription ? (
          <div
            className={css({
              height: '32px',
              width: '32px',
              marginRight: '8px',
              backgroundColor: designColors.accentBlueLight,
              borderRadius: '4px',
            })}
          />
        ) : (
          <img
            src={query.data.tipJarSubscription.podcast.coverArtThumbnail}
            alt=""
            aria-hidden="true"
            className={css({
              borderRadius: '4px',
              marginRight: '8px',
              border: `1px solid ${designColors.borderGrayNormal}`,
            })}
            height={32}
            width={32}
          />
        )}

        <h1
          className={css({
            margin: 0,
            fontSize: '24px',
            fontWeight: 500,
            lineHeight: '28px',
          })}
        >
          {query.loading ? (
            <InlineSkeleton style={{maxWidth: '300px'}} width="300px" />
          ) : query.error || !query.data.tipJarSubscription ? (
            <FormattedMessage {...messages.error} />
          ) : (
            query.data.tipJarSubscription.podcast.name
          )}
        </h1>
      </div>
      <ChromeSidebar
        links={
          <>
            <ChromeSidebarItem
              labelMessage={intl => intl.formatMessage(messages.overviewTab)}
              path={`/subscriptions/recurring_tips/${id}`}
            />
            <ChromeSidebarItem
              labelMessage={intl => intl.formatMessage(messages.privateTab)}
              exact={false}
              path={`/subscriptions/recurring_tips/${id}/listen`}
            />
            <ChromeSidebarItem
              labelMessage={intl => intl.formatMessage(messages.feedbackTab)}
              path={`/subscriptions/recurring_tips/${id}/feedback`}
            />
            <ChromeSidebarItem
              labelMessage={intl => intl.formatMessage(messages.updatesTab)}
              path={`/subscriptions/recurring_tips/${id}/updates`}
            />
            <ChromeSidebarItem
              labelMessage={intl => intl.formatMessage(messages.receiptsTab)}
              path={`/subscriptions/recurring_tips/${id}/receipts`}
            />
          </>
        }
      >
        {children}
      </ChromeSidebar>
    </Chrome>
  );
};

const useRecurringDonationPage = () => {
  const id = useGetId();
  const query = useQuery<RecurringDonation, RecurringDonationVariables>(
    RECURRING_DONATION,
    {
      variables: {slug: id},
    },
  );

  const wrap = (
    render: (
      subscription: RecurringDonation_tipJarSubscription,
    ) => React.ReactNode,
  ) =>
    query.loading ? (
      <Section>
        <LoadingState
          title={
            <FormattedMessage
              description="Heading when podcast is loading"
              defaultMessage="Loading podcast details…"
            />
          }
        />
      </Section>
    ) : query.error || !query.data.tipJarSubscription ? (
      <Section>
        <ErrorState
          title={
            <FormattedMessage
              description="Heading when podcast could not be loaded"
              defaultMessage="Error loading subscription details"
            />
          }
          copy={
            <FormattedMessage
              description="Copy when podcast could not be loaded"
              defaultMessage="We weren't able to load details about this recurring tip."
            />
          }
        />
      </Section>
    ) : (
      render(query.data.tipJarSubscription)
    );

  return {
    id,
    query,
    wrap,
  };
};
const useSupplemental = (id: string) => {
  const supplemental = useQuery<
    RecurringDonationSupplemental,
    RecurringDonationSupplementalVariables
  >(RECURRING_DONATION_SUPPLEMENTAL, {
    variables: {slug: id},
  });
  return supplemental;
};

export const RecurringDonationPageIndex = () => {
  const {id, wrap} = useRecurringDonationPage();
  const supplemental = useSupplemental(id);

  return (
    <RecurringDonationPageWrapper>
      <DesktopPageHeader>
        <FormattedMessage {...messages.overviewTab} />
      </DesktopPageHeader>
      {wrap(subscription => (
        <>
          <Section>
            <RecurringDonationHeader
              subscription={subscription}
              supplemental={supplemental}
            />
          </Section>
          <Section>
            <RecurringDonationPaymentMethod
              subscription={subscription}
              supplemental={supplemental}
            />
          </Section>
          {subscription.eligibleForPrivateContent && (
            <Section>
              <FormattedMessage
                description="Link to premium content"
                defaultMessage="Your subscription gets you access to premium content for this podcast. <listenLink>See what is available</listenLink>."
                values={{
                  listenLink: (...a) => (
                    <Link href={`/subscriptions/recurring_tips/${id}/listen`}>
                      {a}
                    </Link>
                  ),
                }}
              />
            </Section>
          )}
        </>
      ))}
    </RecurringDonationPageWrapper>
  );
};

export const RecurringDonationPageListen = () => {
  const {wrap} = useRecurringDonationPage();
  return (
    <RecurringDonationPageWrapper>
      <DesktopPageHeader>
        <FormattedMessage {...messages.privateTab} />
      </DesktopPageHeader>
      {wrap(subscription => (
        <RecurringDonationPagePrivateContent subscription={subscription} />
      ))}
    </RecurringDonationPageWrapper>
  );
};

export const RecurringDonationPageListenEpisode = () => {
  return (
    <RecurringDonationPageWrapper>
      <RecurringDonationPageListenEpisodeInner />
    </RecurringDonationPageWrapper>
  );
};

export const RecurringDonationPageFeedback = () => {
  const {wrap} = useRecurringDonationPage();
  return (
    <RecurringDonationPageWrapper>
      <DesktopPageHeader>
        <FormattedMessage {...messages.feedbackTab} />
      </DesktopPageHeader>
      {wrap(subscription => (
        <RecurringDonationPageFeedbackInner subscription={subscription} />
      ))}
    </RecurringDonationPageWrapper>
  );
};

export const RecurringDonationPageUpdates = () => {
  const {wrap} = useRecurringDonationPage();
  return (
    <RecurringDonationPageWrapper>
      <DesktopPageHeader>
        <FormattedMessage {...messages.updatesTab} />
      </DesktopPageHeader>
      {wrap(subscription => (
        <RecurringDonationPageUpdatesInner subscription={subscription} />
      ))}
    </RecurringDonationPageWrapper>
  );
};

export const RecurringDonationPageReceipts = () => {
  const id = useGetId();
  const supplemental = useSupplemental(id);
  return (
    <RecurringDonationPageWrapper>
      <DesktopPageHeader>
        <FormattedMessage {...messages.receiptsTab} />
      </DesktopPageHeader>
      <Section>
        <RecurringDonationReceiptsInner supplemental={supplemental} />
      </Section>
    </RecurringDonationPageWrapper>
  );
};
